(() => {
  const supportsIO = "IntersectionObserver" in window;

  function getScrollParent(el) {
    let parent = el.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === "auto" || overflowY === "scroll") &&
        parent.scrollHeight > parent.clientHeight;

      if (isScrollable) return parent;
      parent = parent.parentElement;
    }
    return null; // fallback = viewport
  }

  function getNextUrlFromDoc(doc) {
    // Prefer our wrapper on the next page
    const w = doc.querySelector(".infinite-scroll");
    if (w?.dataset?.nextUrl) return w.dataset.nextUrl;

    // Fallback: <link rel="next">
    const relNext = doc.querySelector('link[rel="next"]')?.getAttribute("href");
    if (relNext) return relNext;

    // Fallback: common pagination selectors
    const nextA =
      doc.querySelector('.pagination a[aria-label*="next"]') ||
      doc.querySelector(".pagination a.pagination__item--prev") ||
      doc.querySelector('.pagination a[href*="page="]');

    return nextA?.getAttribute("href") || null;
  }

  class InfiniteScroll {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.targetSelector = wrapper.dataset.target || "#product-grid";
      this.grid = document.querySelector(this.targetSelector);

      this.loader = wrapper.querySelector(".infinite-scroll__loader");
      this.btn = wrapper.querySelector(".infinite-scroll__btn");
      this.sentinel = wrapper.querySelector(".infinite-scroll__sentinel");

      this.nextUrl = wrapper.dataset.nextUrl || null;
      this.loading = false;
      this.observer = null;

      if (!this.grid || !this.nextUrl) return;

      // Button fallback (also used if IO fails)
      if (this.btn) {
        if (!supportsIO) this.btn.hidden = false;
        this.btn.addEventListener("click", () => this.loadNext());
      }

      // IntersectionObserver (use correct scroll root if needed)
      if (supportsIO && this.sentinel) {
        const scrollParent = getScrollParent(this.sentinel);
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) this.loadNext();
          },
          {
            root: scrollParent || null,
            threshold: 0.1,
            rootMargin: "900px 0px",
          }
        );
        this.observer.observe(this.sentinel);
      }

      // Scroll fallback (covers cases where IO doesn't fire)
      this.onScroll = this.throttle(() => {
        if (this.loading || !this.nextUrl) return;
        const rect = this.wrapper.getBoundingClientRect();
        if (rect.top < window.innerHeight + 700) this.loadNext();
      }, 200);

      window.addEventListener("scroll", this.onScroll, { passive: true });
      // Trigger once in case the page is already near bottom
      this.onScroll();
    }

    throttle(fn, wait) {
      let last = 0;
      return (...args) => {
        const now = Date.now();
        if (now - last >= wait) {
          last = now;
          fn.apply(this, args);
        }
      };
    }

    setLoading(state) {
      this.loading = state;
      if (this.loader) this.loader.hidden = !state;
      if (this.btn) this.btn.disabled = state;
      this.wrapper.classList.toggle("is-loading", state);
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.setLoading(true);

      try {
        const res = await fetch(this.nextUrl, { credentials: "same-origin" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        const newGrid = doc.querySelector(this.targetSelector);
        if (!newGrid) throw new Error(`Grid not found: ${this.targetSelector}`);

        const newItems = Array.from(newGrid.children);
        if (!newItems.length) throw new Error("No items found on next page.");

        newItems.forEach((el) => {
          el.classList.add("is-newly-added");
          this.grid.appendChild(el);
        });

        requestAnimationFrame(() => {
          newItems.forEach((el) => el.classList.add("is-visible"));
        });

        const next = getNextUrlFromDoc(doc);
        this.nextUrl = next;
        this.wrapper.dataset.nextUrl = next || "";

        if (!this.nextUrl) this.destroy();
      } catch (err) {
        console.error("Infinite scroll failed:", err);
        if (this.btn) this.btn.hidden = false; // allow manual retry
      } finally {
        this.setLoading(false);
      }
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener("scroll", this.onScroll);
      if (this.wrapper) this.wrapper.remove();
    }
  }

  function init() {
    document.querySelectorAll(".infinite-scroll").forEach((el) => {
      if (el.dataset.infiniteInit === "1") return;
      el.dataset.infiniteInit = "1";
      new InfiniteScroll(el);
    });
  }

  document.addEventListener("DOMContentLoaded", init);

  // If sections reload (filters/sort), re-init
  document.addEventListener("shopify:section:load", init);

  // MutationObserver safety net
  const mo = new MutationObserver(() => init());
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
